import React, { FC, Suspense, useEffect, useMemo, useState } from 'react'
import { Navigate, Route, RouteProps, Routes, RoutesProps } from 'react-router'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useStyles } from '../lib/styles/universal'
import { LoadingView, ScrollToTop, SiteFooter, SiteHeader } from '../components'
import { RootWelcomePage } from '../pages/RootWelcome'
import {
    getOrganizations,
    getPublicProfilePicture,
    getPublicUser,
    logout,
    membershipOrganizationsFromResponse,
    buildRpTenantHomeUrl,
} from '../lib/utils'
import {
    GetRPOrganizationsResponse,
    RPOrganizationOverview,
    User,
} from '@hazadapt-git/public-core-base'
import { getEnvVars } from '../lib/config'

const InterestPage = React.lazy(() => import('../pages/Interest'))
const SplashPage = React.lazy(() => import('../pages/Splash'))

const { rpUriBase } = getEnvVars()

const RootOrgFetchFailure: FC = () => (
        // TODO: Add some better styling to this.
    <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ flex: 1, px: 2, py: 4 }}
    >
        <Typography variant="body1" textAlign="center">
            We couldn't load organization membership for your account. Try
            again or sign out and use a different HazAdapt ID.
        </Typography>
        <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => window.location.reload()}>
                Try again
            </Button>
            <Button variant="outlined" onClick={() => logout(window.location.origin)}>
                Sign out
            </Button>
        </Stack>
    </Stack>
)

const RootNoOrganizations: FC = () => (
    // TODO: Add some better styling to this.
    <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ flex: 1, px: 2, py: 4 }}
    >
        <Typography variant="body1" textAlign="center">
            Your HazAdapt ID is not assigned to any ResiliencePoint organizations.
            You can request access to get started, or sign out to use another
            account.
        </Typography>
        <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => (window.location.href = '/interest')}>
                Request Access
            </Button>
            <Button variant="outlined" onClick={() => logout(window.location.origin)}>
                Sign out
            </Button>
        </Stack>
    </Stack>
)

const RootLanding: FC<{
    loadingRoot: boolean
    getOrgResponse: GetRPOrganizationsResponse | undefined
    publicUser: User | undefined
    userOrganizations: RPOrganizationOverview[] | undefined
    organizationsFetchFailed: boolean
}> = ({
    loadingRoot,
    getOrgResponse,
    publicUser,
    userOrganizations,
    organizationsFetchFailed,
}) => {
    if (!loadingRoot && !publicUser) {
        return <SplashPage loading={false} />
    }

    if (!loadingRoot && publicUser && organizationsFetchFailed) {
        return <RootOrgFetchFailure />
    }

    if (
        !loadingRoot &&
        publicUser &&
        userOrganizations !== undefined &&
        userOrganizations.length === 0
    ) {
        return <RootNoOrganizations />
    }

    if (
        !loadingRoot &&
        publicUser &&
        userOrganizations !== undefined &&
        userOrganizations.length === 1
    ) {
        return <LoadingView />
    }

    return (
        <RootWelcomePage
            loading={loadingRoot}
            orgResponse={getOrgResponse}
            publicUser={publicUser}
        />
    )
}

export const RootRouter: FC<RoutesProps> = (props: RoutesProps) => {
    const [loading, setLoading] = useState<boolean>(true)
    const [getOrgResponse, setGetOrgResponse] =
        useState<GetRPOrganizationsResponse>()
    const [publicUser, setPublicUser] = useState<User>()
    const [publicPfp, setPublicPfp] = useState<string>()
    const [organizationsFetchFailed, setOrganizationsFetchFailed] =
        useState(false)

    const { classes } = useStyles()

    useEffect(() => {
        const pubUserPromise = getPublicUser()
            .then(setPublicUser)
            .catch((err) => {
                console.error(err)
            })
        const orgPromise = getOrganizations()
            .then(setGetOrgResponse)
            .catch((err) => {
                console.error(err)
                setOrganizationsFetchFailed(true)
            })
        const pubProfilePicturePromise = getPublicProfilePicture()
            .then(setPublicPfp)
            .catch((err) => {
                console.error(err)
            })
        Promise.all([
            pubUserPromise,
            orgPromise,
            pubProfilePicturePromise,
        ]).finally(() => setLoading(false))
    }, [])

    const userOrganizations = useMemo(() => {
        if (!publicUser || !getOrgResponse || organizationsFetchFailed) {
            return undefined
        }
        return membershipOrganizationsFromResponse(getOrgResponse)
    }, [organizationsFetchFailed, publicUser, getOrgResponse])

    useEffect(() => {
        if (!userOrganizations || userOrganizations.length !== 1 || !rpUriBase) {
            return
        }
        const slug = userOrganizations[0].slug
        window.location.href = buildRpTenantHomeUrl(slug, rpUriBase)
    }, [userOrganizations])

    const routes: RouteProps[] = [
        {
            path: '/interest',
            element: <InterestPage loading={false} />,
        },
        {
            path: '/',
            element: (
                <RootLanding
                    loadingRoot={loading}
                    getOrgResponse={getOrgResponse}
                    publicUser={publicUser}
                    userOrganizations={userOrganizations}
                    organizationsFetchFailed={organizationsFetchFailed}
                />
            ),
        },
        // Catch-all; keep this as the last route in the array
        {
            path: '*',
            element: <Navigate replace to="/" />,
        },
    ]

    return (
        <ScrollToTop>
            <div className={classes.root}>
                <SiteHeader
                    publicUser={publicUser}
                    publicProfilePicture={publicPfp}
                    hideHamburgerMenu
                />
                <div className={classes.page}>
                    {loading ? (
                        <LoadingView />
                    ) : (
                        <Suspense fallback={<LoadingView />}>
                            <Routes {...props}>
                                {routes.map((route: RouteProps) => (
                                    <Route key={route.path} {...route} />
                                ))}
                            </Routes>
                        </Suspense>
                    )}
                </div>
                <SiteFooter />
            </div>
        </ScrollToTop>
    )
}
